import { CANONICAL_ORIGIN, DEFAULT_OG_IMAGE } from "./siteSeo";

const ORGANIZATION_ID = `${CANONICAL_ORIGIN}/#organization`;
const WEBSITE_ID = `${CANONICAL_ORIGIN}/#website`;

export function buildOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: "Lesson Launchpad",
    url: CANONICAL_ORIGIN,
    logo: `${CANONICAL_ORIGIN}/square-logo-v2.png`,
    sameAs: [
      "https://www.facebook.com/lessonlaunchpad",
      "https://www.youtube.com/@LessonLaunchpad",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      url: `${CANONICAL_ORIGIN}/contact`,
    },
  };
}

export function buildWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: "Lesson Launchpad",
    url: CANONICAL_ORIGIN,
    publisher: { "@id": ORGANIZATION_ID },
    potentialAction: {
      "@type": "SearchAction",
      target: `${CANONICAL_ORIGIN}/tools?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function buildBreadcrumbSchema(items) {
  if (!items?.length) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${CANONICAL_ORIGIN}${item.url}`,
    })),
  };
}

export function buildWebApplicationSchema({
  name,
  description,
  url,
  category = "EducationalApplication",
  offers,
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name,
    description,
    url: url.startsWith("http") ? url : `${CANONICAL_ORIGIN}${url}`,
    applicationCategory: category,
    operatingSystem: "Web",
    browserRequirements: "Requires JavaScript",
    offers: offers || {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      description: "Free trial available",
      url: `${CANONICAL_ORIGIN}/auth/register`,
    },
    provider: { "@id": ORGANIZATION_ID },
    image: DEFAULT_OG_IMAGE,
  };
}

export function buildBlogPostingSchema({ title, description, url, datePublished, image }) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    url: url.startsWith("http") ? url : `${CANONICAL_ORIGIN}${url}`,
    datePublished: datePublished || undefined,
    image: image || DEFAULT_OG_IMAGE,
    author: {
      "@type": "Organization",
      name: "Lesson Launchpad",
    },
    publisher: {
      "@type": "Organization",
      name: "Lesson Launchpad",
      logo: {
        "@type": "ImageObject",
        url: `${CANONICAL_ORIGIN}/square-logo-v2.png`,
      },
    },
  };
}

export function buildArticleSchema({ title, description, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    url: url.startsWith("http") ? url : `${CANONICAL_ORIGIN}${url}`,
    author: {
      "@type": "Organization",
      name: "Lesson Launchpad",
    },
    publisher: { "@id": ORGANIZATION_ID },
    image: DEFAULT_OG_IMAGE,
  };
}

export function buildFaqSchema(faqs) {
  if (!faqs?.length) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.schemaAnswer || faq.answer,
      },
    })),
  };
}

export function buildGlobalSchemas() {
  return [buildOrganizationSchema(), buildWebSiteSchema()];
}

/**
 * Safari throws TypeError on top-level JSON-LD arrays
 * (`r["@context"].toLowerCase`). Emit a single object, using @graph when
 * there are multiple schemas.
 * @see https://bugs.webkit.org/show_bug.cgi?id=255764
 */
export function serializeStructuredData(schemas) {
  const items = (schemas || []).filter(Boolean);
  if (items.length === 0) {
    return null;
  }

  if (items.length === 1) {
    return items[0];
  }

  return {
    "@context": "https://schema.org",
    "@graph": items.map(({ "@context": _context, ...rest }) => rest),
  };
}
